import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApprovalDialog } from '@/components/efs/ApprovalDialog';
import { PurgeMasterDialog } from '@/components/efs/PurgeMasterDialog';
import { useCurrentUser } from '@/lib/auth-context';
import { canApproveMasters, hasPermission } from '@/lib/permissions';
import { IncomingItemsFormDialog, type IncomingItemsDialogMode } from './IncomingItemsFormDialog';
import {
  useActivateIncomingItem,
  useApproveIncomingItem,
  usePurgeIncomingItem,
  useRejectIncomingItem,
  useRetireIncomingItem,
} from './useIncomingItems';
import type { IncomingItem } from './types';

interface IncomingItemsRowActionsProps {
  row: IncomingItem;
}

export const IncomingItemsRowActions = ({ row }: IncomingItemsRowActionsProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const approve = useApproveIncomingItem();
  const reject = useRejectIncomingItem();
  const retire = useRetireIncomingItem();
  const activate = useActivateIncomingItem();
  const purge = usePurgeIncomingItem();
  const [intent, setIntent] = useState<'approve' | 'reject' | null>(null);
  const [editMode, setEditMode] = useState<IncomingItemsDialogMode | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const canApprove = canApproveMasters(user);
  const canDirectEdit = hasPermission(user, 'master.approve');
  const canPropose = hasPermission(user, 'master.update');

  // Phase 2 review (P1-#3): DB uses `draft` for supervisor-created rows that
  // haven't been routed yet, and `pending_approval` once an audit row exists.
  // Both should expose the manager approve/reject actions.
  const isPending = row.approval_status === 'pending_approval' || row.approval_status === 'draft';
  const isApproved = row.approval_status === 'approved';

  const showApprove = canApprove && isPending;
  const showReject = canApprove && isPending;
  const showDirect = canDirectEdit && isApproved;
  const showPropose = !canDirectEdit && canPropose && isApproved;
  const showRetire = canDirectEdit && isApproved && row.active_flag;
  const showActivate = canDirectEdit && !row.active_flag;
  const showPurge = canDirectEdit && !row.active_flag;

  if (
    !showApprove &&
    !showReject &&
    !showDirect &&
    !showPropose &&
    !showRetire &&
    !showActivate &&
    !showPurge
  )
    return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('efs.masters.actions.row_menu', { defaultValue: 'アクション' })}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showApprove ? (
            <DropdownMenuItem onSelect={() => setIntent('approve')}>
              {t('efs.masters.actions.approve', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showReject ? (
            <DropdownMenuItem onSelect={() => setIntent('reject')}>
              {t('efs.masters.actions.reject', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showDirect ? (
            <DropdownMenuItem onSelect={() => setEditMode('direct')}>
              {t('efs.masters.actions.edit_direct', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showPropose ? (
            <DropdownMenuItem onSelect={() => setEditMode('proposal')}>
              {t('efs.masters.actions.propose_change', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showRetire ? (
            <DropdownMenuItem
              onSelect={() => retire.mutate(row.incoming_item_id)}
              disabled={retire.isPending}
            >
              {t('efs.masters.actions.deactivate', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showActivate ? (
            <DropdownMenuItem
              onSelect={() => activate.mutate(row.incoming_item_id)}
              disabled={activate.isPending}
            >
              {t('efs.masters.actions.activate', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
          {showPurge ? (
            <DropdownMenuItem
              onSelect={() => {
                purge.reset();
                setPurgeOpen(true);
              }}
              className="text-red-900 focus:text-red-900"
            >
              {t('efs.masters.actions.purge', { ns: 'masters' })}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <ApprovalDialog
        open={intent !== null}
        onOpenChange={(open) => {
          if (!open) setIntent(null);
        }}
        intent={intent ?? 'approve'}
        isSubmitting={approve.isPending || reject.isPending}
        requireReason={intent === 'reject'}
        onConfirm={async (reason) => {
          if (intent === 'approve') {
            await approve.mutateAsync({ id: row.incoming_item_id, reason });
          } else if (intent === 'reject') {
            await reject.mutateAsync({ id: row.incoming_item_id, reason });
          }
          setIntent(null);
        }}
      />

      <PurgeMasterDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        name={row.japanese_name}
        isPending={purge.isPending}
        error={purge.error}
        onConfirm={() =>
          purge.mutate(row.incoming_item_id, { onSuccess: () => setPurgeOpen(false) })
        }
      />

      <IncomingItemsFormDialog
        open={editMode !== null}
        onOpenChange={(open) => {
          if (!open) setEditMode(null);
        }}
        row={editMode !== null ? row : null}
        mode={editMode ?? 'direct'}
      />
    </>
  );
};
